import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { generateSlug } from '../../utils/helpers';

const CategoriesManager = () => {
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    color: '#FACC15',
    icon: '',
    order_position: 0,
    is_active: true
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await api.get('/categories/all');
      if (response.data.success) {
        setCategories(response.data.categories);
      }
    } catch (error) {
      toast.error('Erreur de chargement');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingCategory) {
        await api.put(`/categories/${editingCategory.id}`, formData);
        toast.success('Catégorie modifiée');
      } else {
        await api.post('/categories', formData);
        toast.success('Catégorie créée');
      }
      
      setShowModal(false);
      setEditingCategory(null);
      resetForm();
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Supprimer cette catégorie ?')) return;
    
    try {
      await api.delete(`/categories/${id}`);
      toast.success('Catégorie supprimée');
      loadCategories();
    } catch (error) {
      toast.error('Erreur de suppression');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData(category);
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      color: '#FACC15',
      icon: '',
      order_position: 0,
      is_active: true
    });
  };

  return (
    <AdminLayout>
      <Helmet><title>Catégories - Admin Blog</title></Helmet>
      
      <div className="p-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-white">Catégories</h1>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="bg-yellow-400 text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-yellow-500"
          >
            <i className="fas fa-plus mr-2"></i>Nouvelle catégorie
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map(category => (
            <div key={category.id} className="bg-surface rounded-xl p-6 border border-gray-700/30">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: category.color }}
                  >
                    <i className={`fas fa-${category.icon || 'folder'} text-gray-900`}></i>
                  </div>
                  <div>
                    <h3 className="text-white font-bold">{category.name}</h3>
                    <p className="text-gray-500 text-xs">/{category.slug}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  category.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                }`}>
                  {category.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              {category.description && (
                <p className="text-gray-400 text-sm mb-4">{category.description}</p>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(category)}
                  className="flex-1 bg-blue-500/20 text-blue-400 py-2 rounded-lg hover:bg-blue-500/30"
                >
                  Modifier
                </button>
                <button
                  onClick={() => handleDelete(category.id)}
                  className="px-4 bg-red-500/20 text-red-400 py-2 rounded-lg hover:bg-red-500/30"
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-surface rounded-xl p-6 max-w-lg w-full">
              <h2 className="text-2xl font-bold text-white mb-4">
                {editingCategory ? 'Modifier' : 'Nouvelle'} catégorie
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="text"
                  placeholder="Nom"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value, slug: generateSlug(e.target.value) })}
                  className="w-full px-4 py-3 bg-surface/90 border border-gray-700 rounded-lg text-white"
                  required
                />
                <input
                  type="text"
                  placeholder="Slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  className="w-full px-4 py-3 bg-surface/90 border border-gray-700 rounded-lg text-white"
                />
                <textarea
                  placeholder="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-3 bg-surface/90 border border-gray-700 rounded-lg text-white"
                  rows="3"
                />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-gray-400 text-sm">Couleur</label>
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="w-full h-12 bg-surface/90 border border-gray-700 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm">Icône (Font Awesome)</label>
                    <input
                      type="text"
                      placeholder="newspaper"
                      value={formData.icon}
                      onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                      className="w-full px-4 py-3 bg-surface/90 border border-gray-700 rounded-lg text-white"
                    />
                  </div>
                </div>
                <label className="flex items-center text-gray-300">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="mr-2"
                  />
                  Catégorie active
                </label>
                <div className="flex gap-3 pt-4">
                  <button type="submit" className="flex-1 bg-yellow-400 text-gray-900 py-3 rounded-lg font-semibold hover:bg-yellow-500">
                    {editingCategory ? 'Mettre à jour' : 'Créer'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowModal(false); setEditingCategory(null); }}
                    className="px-6 bg-gray-700 text-white py-3 rounded-lg hover:bg-gray-600"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default CategoriesManager;
